import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProperty, resolveAsset } from '../context/PropertyContext';
import { Menu, LogOut, LayoutDashboard, CalendarCheck, Wallet, Clock3, BookText, Users, QrCode, Settings as SettingsIcon, ScrollText, CalendarDays } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';
import { RegisterFreshness, StaleBanner } from './RegisterFreshness';

const NAV = [
  { to: '/', label: 'Today', icon: LayoutDashboard, roles: ['front_desk', 'finance', 'manager'] },
  { to: '/arrivals', label: 'Arrivals', icon: CalendarCheck, roles: ['front_desk', 'manager'] },
  { to: '/scanner', label: 'Scanner', icon: QrCode, roles: ['front_desk', 'manager'] },
  { to: '/payments', label: 'Payments', icon: Wallet, roles: ['finance', 'manager'] },
  { to: '/holds', label: 'Holds at risk', icon: Clock3, roles: ['finance', 'manager'] },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays, roles: ['front_desk', 'finance', 'manager'] },
  { to: '/register', label: 'Register', icon: BookText, roles: ['manager'] },
  { to: '/audit', label: 'Audit log', icon: ScrollText, roles: ['manager'] },
  { to: '/staff', label: 'Staff', icon: Users, roles: ['manager'] },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, roles: ['manager'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { property } = useProperty() || {};
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const onLogout = async () => {
    await logout();
    nav('/login');
  };

  const roleLabel = { front_desk: 'Front Desk', finance: 'Finance', manager: 'Manager' }[user?.role] || '';
  const propName = property?.name || 'Front Desk Console';
  const logoUrl = property?.logo_url ? resolveAsset(property.logo_url) : '';

  return (
    <div className="app-bg">
      <Toaster position="top-right" richColors />
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#FBFCFB]/90 backdrop-blur border-b border-[#CFD8CF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <button
            data-testid="menu-toggle"
            className="lg:hidden p-2 rounded-lg hover:bg-[#E8F5E9]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2 select-none">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-9 h-9 rounded-lg object-contain bg-white border border-[#CFD8CF]" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-[#2E7D32] text-white grid place-items-center font-heading font-bold" aria-hidden>
                {propName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="leading-tight">
              <div className="font-heading font-medium text-[#1F5A24] text-lg">{propName}</div>
              <div className="text-[11px] uppercase tracking-widest text-[#333]/60">Front Desk Console</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <RegisterFreshness />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <div data-testid="current-user" className="text-sm font-medium">{user?.name || user?.email}</div>
              <div className="text-xs text-[#333]/60">{roleLabel}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#CFD8CF] hover:bg-[#E8F5E9] text-sm"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex gap-6">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block w-56 shrink-0">
          <nav className="sticky top-24 flex flex-col gap-1">
            {items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-[#E8F5E9] text-[#1F5A24]' : 'text-[#333] hover:bg-[#E8F5E9]/60'
                  }`
                }
              >
                <Icon size={18} /> {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-40" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/30" />
            <nav className="absolute left-0 top-0 bottom-0 w-72 bg-[#FBFCFB] border-r border-[#CFD8CF] p-4 flex flex-col gap-1 animate-slide-in-right" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 px-2">
                <div className="font-heading text-lg text-[#1F5A24]">{propName}</div>
                <div className="text-xs text-[#333]/60">{roleLabel}</div>
              </div>
              {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  onClick={() => setOpen(false)}
                  data-testid={`mnav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium ${
                      isActive ? 'bg-[#E8F5E9] text-[#1F5A24]' : 'text-[#333]'
                    }`
                  }
                >
                  <Icon size={18} /> {label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <StaleBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
